import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Paper, 
  Typography, 
  Box, 
  Grid, 
  Chip,
  Stack,
  Divider,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { useLocation, useNavigate } from 'react-router-dom';
import { getProcedureQuestionnaireTemplate, getLatestProcedureQuestionnaireTemplate } from './procedureQuestionnaireTemplate';
import ProcedureVersionBadge from './ProcedureVersionBadge';
import ProcedureForm from './ProcedureForm';
import '../../styles/FHIRQuestionnaireView.css';

const ProcedureView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { procedure: initialProcedure, patient } = location.state || {};
  
  const [procedure, setProcedure] = useState(initialProcedure);
  const [responses, setResponses] = useState({});
  const [currentTemplate, setCurrentTemplate] = useState(getLatestProcedureQuestionnaireTemplate());
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (procedure?.procedure_id) {
      loadProcedureDetails();
    }
  }, [procedure?.procedure_id]);

  const loadProcedureDetails = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/procedures/${procedure.procedure_id}`);
      setProcedure(data);
      
      if (data.questionnaire_data) {
        setResponses(data.questionnaire_data);
      }
      
      // Load the appropriate template version
      if (data.template_version) {
        const versionedTemplate = getProcedureQuestionnaireTemplate(data.template_version);
        setCurrentTemplate(versionedTemplate);
      }
    } catch (error) {
      console.error('Error loading procedure details:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleString();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'info';
      case 'planned': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const renderResponseValue = (value, item) => {
    if (!value && value !== false) return <em>Not answered</em>;
    
    if (item?.type === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    
    if (item?.type === 'signature') {
      return (
        <Box sx={{ 
          border: 1, 
          borderColor: 'grey.300',
          borderRadius: 1,
          p: 1.5,
          bgcolor: 'grey.50',
          minHeight: 60,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {value ? (
            // Check if it's base64 image data
            value.startsWith('data:image/') ? (
              <Box sx={{ textAlign: 'center' }}>
                <img 
                  src={value} 
                  alt="Signature" 
                  style={{ 
                    maxWidth: '200px', 
                    maxHeight: '60px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }} 
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Digital Signature
                </Typography>
              </Box>
            ) : (
              // For encoded/encrypted signature data, show a signature indicator
              <Box sx={{ textAlign: 'center' }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontFamily: 'cursive',
                    fontSize: '1.4rem',
                    color: 'primary.main',
                    fontWeight: 'bold',
                    mb: 0.5
                  }}
                >
                  ✓ Digitally Signed
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Electronic signature on file
                </Typography>
              </Box>
            )
          ) : (
            <Typography 
              variant="caption" 
              color="text.disabled"
              sx={{ fontStyle: 'italic' }}
            >
              No signature provided
            </Typography>
          )}
        </Box>
      );
    }
    
    if (item?.type === 'date') {
      return formatDate(value);
    }
    
    if (item?.type === 'datetime-local') {
      return formatDateTime(value);
    }
    
    if (typeof value === 'string' && value.length > 100) {
      return (
        <Box>
          <Typography variant="body2" component="div">
            {value.split('\n').map((line, index) => (
              <React.Fragment key={index}>
                {line}
                {index < value.split('\n').length - 1 && <br />}
              </React.Fragment>
            ))}
          </Typography>
        </Box>
      );
    }
    
    return value;
  };

  const validateSection = (section) => {
    let hasAnyContent = false;
    
    const checkItems = (items) => {
      items.forEach(item => {
        if (item.type === 'group') {
          checkItems(item.item || []);
        } else {
          const response = responses[item.linkId];
          if (response !== undefined && response !== null && response !== '' && response !== false) {
            hasAnyContent = true;
          }
        }
      });
    };
    
    checkItems(section.item || []);
    
    // For consumables and post-op sections, require content to mark as complete
    const sectionId = section.linkId;
    if ((sectionId === 'intraOpConsumables' || sectionId === 'postOperative') && !hasAnyContent) {
      return false;
    }
    
    return hasAnyContent;
  };

  const getOverallCompletionStatus = () => {
    if (!currentTemplate.item || currentTemplate.item.length === 0) {
      return { isComplete: false, completedSections: 0, totalSections: 0 };
    }
    
    const totalSections = currentTemplate.item.length;
    let completedSections = 0;
    
    currentTemplate.item.forEach(section => {
      if (validateSection(section)) {
        completedSections++;
      }
    });
    
    return {
      isComplete: completedSections === totalSections && totalSections > 0,
      completedSections,
      totalSections
    };
  };

  const renderQuestionnaireSection = (section) => {
    const renderQuestionItems = (items, level = 0) => {
      return items.map(item => {
        if (item.type === 'group') {
          return (
            <Box key={item.linkId} sx={{ mb: 3, ml: level * 2 }}>
              <Typography 
                variant={level === 0 ? "h6" : "subtitle1"} 
                color="primary" 
                gutterBottom
                sx={{ fontWeight: 'bold' }}
              >
                {item.text}
              </Typography>
              <Box sx={{ ml: 2 }}>
                {renderQuestionItems(item.item || [], level + 1)}
              </Box>
            </Box>
          );
        }

        const response = responses[item.linkId];
        const hasResponse = response !== undefined && response !== null && response !== '';

        // Special layout for signature fields
        if (item.type === 'signature') {
          return (
            <Box key={item.linkId} sx={{ mb: 3, ml: level * 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                {item.text}:
              </Typography>
              {renderResponseValue(response, item)}
            </Box>
          );
        }

        return (
          <Box key={item.linkId} sx={{ mb: 2, ml: level * 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'medium' }}>
                  {item.text}:
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <Typography 
                  variant="body2" 
                  color={hasResponse ? "text.primary" : "text.disabled"}
                  sx={{ fontStyle: hasResponse ? 'normal' : 'italic' }}
                >
                  {renderResponseValue(response, item)}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        );
      });
    };

    // Special styling for signatures section
    const isSignatureSection = section.linkId === 'signatures';
    
    return (
      <Card 
        key={section.linkId} 
        sx={{ 
          mb: 3,
          ...(isSignatureSection && {
            border: 2,
            borderColor: 'primary.main',
            bgcolor: 'primary.50'
          })
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography 
              variant="h5" 
              color="primary" 
              sx={{ 
                flexGrow: 1,
                ...(isSignatureSection && {
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                })
              }}
            >
              {isSignatureSection && '📝'} {section.text}
            </Typography>
            {validateSection(section) ? (
              <CheckCircleIcon color="success" />
            ) : (
              <Box sx={{ 
                width: 24, 
                height: 24, 
                borderRadius: '50%', 
                border: 2, 
                borderColor: 'grey.300',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Box sx={{ 
                  width: 8, 
                  height: 8, 
                  borderRadius: '50%', 
                  bgcolor: 'grey.300'
                }} />
              </Box>
            )}
          </Box>
          <Divider sx={{ mb: 2 }} />
          {isSignatureSection ? (
            <Box sx={{ 
              bgcolor: 'background.paper', 
              p: 2, 
              borderRadius: 1,
              border: 1,
              borderColor: 'grey.200'
            }}>
              {renderQuestionItems(section.item || [])}
            </Box>
          ) : (
            renderQuestionItems(section.item || [])
          )}
        </CardContent>
      </Card>
    );
  };

  const renderProcedureConsumables = () => {
    if (!procedure.consumables || (Array.isArray(procedure.consumables) && procedure.consumables.length === 0)) {
      return <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>No consumables recorded</Typography>;
    }

    const consumables = Array.isArray(procedure.consumables) ? procedure.consumables : [procedure.consumables];
    
    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell>Quantity</TableCell>
              <TableCell>Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {consumables.map((item, index) => (
              <TableRow key={index}>
                <TableCell>{item.name || item.item || 'Unknown Item'}</TableCell>
                <TableCell>{item.quantity || 'N/A'}</TableCell>
                <TableCell>{item.notes || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading procedure details...</Typography>
      </Box>
    );
  }

  if (!procedure) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Procedure not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header Section */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              {procedure.procedure_name}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Chip 
                label={procedure.status?.replace('_', ' ').toUpperCase()} 
                color={getStatusColor(procedure.status)}
                size="small"
              />
              <Chip label={procedure.procedure_type?.toUpperCase()} size="small" />
              <Chip label={procedure.setting?.toUpperCase()} size="small" />
              {(() => {
                const completionStatus = getOverallCompletionStatus();
                return (
                  <Chip 
                    label={completionStatus.isComplete ? 'COMPLETE' : `INCOMPLETE (${completionStatus.completedSections}/${completionStatus.totalSections})`}
                    color={completionStatus.isComplete ? 'success' : 'warning'}
                    size="small"
                    sx={{ fontWeight: 'bold' }}
                  />
                );
              })()}
              <ProcedureVersionBadge version={procedure.template_version} />
            </Stack>
          </Box>
          <Box>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => setIsEditModalOpen(true)}
              sx={{ mr: 1 }}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
          </Box>
        </Box>

        {/* Basic Procedure Information */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom color="primary">
              Procedure Information
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Patient:</Typography>
              <Typography variant="body1">
                {procedure.Patient?.patient_first_name} {procedure.Patient?.patient_last_name} 
                ({procedure.Patient?.patient_number})
              </Typography>
            </Box>
            {procedure.PatientAdmission && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Admission:</Typography>
                <Typography variant="body1">
                  {procedure.PatientAdmission.admission_id} - {procedure.PatientAdmission.ward}
                </Typography>
              </Box>
            )}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Scheduled Date:</Typography>
              <Typography variant="body1">{formatDateTime(procedure.scheduled_date)}</Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Location:</Typography>
              <Typography variant="body1">{procedure.location || 'Not specified'}</Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom color="primary">
              Staff Information
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Primary Surgeon:</Typography>
              <Typography variant="body1">{procedure.primary_surgeon || 'Not specified'}</Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Assisting Staff:</Typography>
              <Typography variant="body1">
                {procedure.assisting_staff || 'Not specified'}
              </Typography>
            </Box>
            {procedure.start_time && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Start Time:</Typography>
                <Typography variant="body1">{formatDateTime(procedure.start_time)}</Typography>
              </Box>
            )}
            {procedure.end_time && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">End Time:</Typography>
                <Typography variant="body1">{formatDateTime(procedure.end_time)}</Typography>
              </Box>
            )}
          </Grid>
        </Grid>

        {/* Summary */}
        {procedure.summary && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom color="primary">
              Summary
            </Typography>
            <Typography variant="body1">
              {procedure.summary}
            </Typography>
          </Box>
        )}

      </Paper>

      {/* Questionnaire Sections */}
      {responses && Object.keys(responses).length > 0 && (
        <Box>
          <Typography variant="h5" gutterBottom color="primary">
            Procedure Documentation
          </Typography>
          {currentTemplate.item?.map(section => renderQuestionnaireSection(section))}
        </Box>
      )}

      {/* Additional Notes */}
      {procedure.notes && (
        <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Additional Notes
          </Typography>
          <Typography variant="body1">
            {procedure.notes}
          </Typography>
        </Paper>
      )}

      {/* Edit Modal */}
      <Dialog 
        open={isEditModalOpen} 
        onClose={() => setIsEditModalOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Edit Procedure
            <IconButton onClick={() => setIsEditModalOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <ProcedureForm 
            procedure={procedure}
            patient={patient || procedure.Patient}
            onClose={() => {
              setIsEditModalOpen(false);
              loadProcedureDetails(); // Refresh data
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ProcedureView;
